import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { 
  Box, 
  TextField, 
  Typography, 
  Autocomplete,
  IconButton,
  Checkbox
} from '@mui/material';
import { Close, CheckBox as CheckBoxIcon, CheckBoxOutlineBlank } from '@mui/icons-material';

import { DocumentType } from '@frontend/features/auth/types';
import { countries } from '@frontend/features/common/constants/countries';
import { CountryFlag } from '@frontend/features/common/components/CountryFlag';
import { FilterItem } from '@frontend/features/common/components/FilterItem';
import { useGetMyAgencies } from '@frontend/features/agencies/queries';
import { useGetAllUsers } from '@frontend/features/users/queries';
import { useGetTokenOrganizations } from '@frontend/features/organizations/queries';

interface Props {
  title: string;
  searchParams: URLSearchParams;
  updateFilters: (filters: Record<string, string | undefined>) => void;
  onClose?: () => void;
}

export const ClientListFiltersOptions = ({ 
  title, 
  searchParams, 
  updateFilters,
  onClose 
}: Props) => {
  const { t } = useTranslation();

  // Estados locales para los filtros
  const [clientName, setClientName] = useState(searchParams.get('clientName') || '');
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    searchParams.get('countries')?.split('|').filter(Boolean) || []
  );
  const [selectedOrganizations, setSelectedOrganizations] = useState<number[]>(
    searchParams.get('organizationIds')?.split('|').filter(Boolean).map(Number) || []
  );
  const [selectedAgencies, setSelectedAgencies] = useState<number[]>(
    searchParams.get('agencyIds')?.split('|').filter(Boolean).map(Number) || []
  );
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<string[]>(
    searchParams.get('documentTypes')?.split('|').filter(Boolean) || []
  );
  const [selectedPreferredAgents, setSelectedPreferredAgents] = useState<string[]>(
    searchParams.get('preferredAgentIds')?.split('|').filter(Boolean) || []
  );
  const [agentSearch, setAgentSearch] = useState('');

  // Data fetching
  const { data: agencies } = useGetMyAgencies({});
  const { data: organizations } = useGetTokenOrganizations();
  
  const { data: agentList } = useGetAllUsers({
    pageSize: 1000,
    currentPage: 1,
    search: agentSearch,
  });

  // Sincronizar con cambios en searchParams
  useEffect(() => {
    setClientName(searchParams.get('clientName') || '');
    setSelectedCountries(searchParams.get('countries')?.split('|').filter(Boolean) || []);
    setSelectedOrganizations(searchParams.get('organizationIds')?.split('|').filter(Boolean).map(Number) || []);
    setSelectedAgencies(searchParams.get('agencyIds')?.split('|').filter(Boolean).map(Number) || []);
    setSelectedDocumentTypes(searchParams.get('documentTypes')?.split('|').filter(Boolean) || []);
    setSelectedPreferredAgents(searchParams.get('preferredAgentIds')?.split('|').filter(Boolean) || []);
  }, [searchParams]);

  // Handlers
  const handleClientNameChange = (value: string) => {
    setClientName(value);
    updateFilters({ clientName: value || undefined });
  };

  const handleCountriesChange = (countries: string[]) => {
    setSelectedCountries(countries);
    updateFilters({ countries: countries.length ? countries.join('|') : undefined });
  };

  const handleOrganizationsChange = (organizationIds: number[]) => {
    setSelectedOrganizations(organizationIds);
    updateFilters({ organizationIds: organizationIds.length ? organizationIds.join('|') : undefined });
  };

  const handleAgenciesChange = (agencyIds: number[]) => {
    setSelectedAgencies(agencyIds);
    updateFilters({ agencyIds: agencyIds.length ? agencyIds.join('|') : undefined });
  };

  const handleDocumentTypesChange = (types: string[]) => {
    setSelectedDocumentTypes(types);
    updateFilters({ documentTypes: types.length ? types.join('|') : undefined });
  };

  const handlePreferredAgentsChange = (agentIds: string[]) => {
    setSelectedPreferredAgents(agentIds);
    updateFilters({ preferredAgentIds: agentIds.length ? agentIds.join('|') : undefined });
  };

  const notSelectedOption = {
    id: 'notselected',
    firstName: t('common.unassigned'),
    lastName: '',
  };

  return (
    <Box flexDirection="column" display="flex" width="400px" pb={2}>
      {/* Header */}
      <Box
        display="flex"
        pl={2}
        pr={2}
        pb={1}
        justifyContent="space-between"
        alignItems="center"
        alignSelf="stretch"
        borderBottom="1px solid var(--divider, rgba(0, 0, 0, 0.12))"
      >
        <Typography variant="subtitle2" gutterBottom={false}>
          {title}
        </Typography>
        {onClose && (
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        )}
      </Box>

      {/* Filtros */}
      <Box display="flex" flexDirection="column" mt={3} gap={3}>
        {/* Nombre del Cliente */}
        <FilterItem
          label={t('clients.filters.clientName')}
          onClear={() => {
            setClientName('');
            updateFilters({ clientName: undefined });
          }}
        >
          <TextField
            size="small"
            value={clientName}
            onChange={(e) => handleClientNameChange(e.target.value)}
            placeholder={t('clients.filters.searchClientNamePlaceholder')}
            fullWidth
          />
        </FilterItem>

        {/* Países */}
        <FilterItem
          label={t('clients.filters.countries')}
          onClear={() => {
            setSelectedCountries([]);
            updateFilters({ countries: undefined });
          }}
        >
          <Autocomplete
            multiple
            size="small"
            fullWidth
            limitTags={1}
            options={countries}
            getOptionLabel={(option) => option.name}
            value={countries.filter(country => selectedCountries.includes(country.name))}
            onChange={(_, value) => {
              handleCountriesChange(value.map(v => v.name));
            }}
            renderInput={(params) => <TextField {...params} variant="outlined" />}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.code}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  <CountryFlag countryCode={option.code} />
                  <Box ml={1}>{option.name}</Box>
                </li>
              );
            }}
          />
        </FilterItem>

        {/* Organizaciones */}
        <FilterItem
          label={t('clients.filters.organizations')}
          onClear={() => {
            setSelectedOrganizations([]);
            updateFilters({ organizationIds: undefined });
          }}
        >
          <Autocomplete
            multiple
            size="small"
            fullWidth
            limitTags={1}
            options={organizations || []}
            getOptionLabel={(option) => option.name}
            value={organizations?.filter(org => selectedOrganizations.includes(org.id)) || []}
            onChange={(_, value) => {
              handleOrganizationsChange(value.map(v => v.id));
            }}
            renderInput={(params) => <TextField {...params} variant="outlined" />}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
          />
        </FilterItem>

        {/* Agencias */}
        <FilterItem
          label={t('clients.filters.agencies')}
          onClear={() => {
            setSelectedAgencies([]);
            updateFilters({ agencyIds: undefined });
          }}
        >
          <Autocomplete
            multiple
            size="small"
            fullWidth
            limitTags={1}
            options={agencies || []}
            getOptionLabel={(option) => option.name}
            value={agencies?.filter(agency => selectedAgencies.includes(agency.id)) || []}
            onChange={(_, value) => {
              handleAgenciesChange(value.map(v => v.id));
            }}
            renderInput={(params) => <TextField {...params} variant="outlined" />}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
          />
        </FilterItem>

        {/* Agente Preferido */}
        <FilterItem
          label={t('clients.filters.preferredAgent')}
          onClear={() => {
            setSelectedPreferredAgents([]);
            updateFilters({ preferredAgentIds: undefined });
          }}
        >
          <Autocomplete
            fullWidth
            limitTags={1}
            size="small"
            multiple
            options={(agentList ? [{ ...notSelectedOption }, ...agentList?.items] : []).slice().sort((a, b) =>
              (a.firstName || '').localeCompare(b.firstName || '')
            )}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            value={
              agentList ? [{ ...notSelectedOption }, ...agentList?.items].filter(
                (option) => selectedPreferredAgents.includes(String(option.id)),
              ) : []
            }
            onChange={(_event, value) => {
              const valuesForUrl = value.map((val) => String(val.id));
              handlePreferredAgentsChange(valuesForUrl);
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" fullWidth />
            )}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option.firstName} {option.lastName}
                </li>
              );
            }}
            inputValue={agentSearch}
            onInputChange={(_event, value) => {
              setAgentSearch(value);
            }}
          />
        </FilterItem>

        {/* Tipos de Documento */}
        <FilterItem
          label={t('clients.filters.documentTypes')}
          onClear={() => {
            setSelectedDocumentTypes([]);
            updateFilters({ documentTypes: undefined });
          }}
        >
          <Autocomplete
            multiple
            size="small"
            fullWidth
            limitTags={1}
            options={Object.values(DocumentType)}
            value={selectedDocumentTypes}
            onChange={(_, value) => handleDocumentTypesChange(value)}
            renderInput={(params) => <TextField {...params} variant="outlined" />}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps} key={option}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    sx={{ mr: 1 }}
                    checked={selected}
                  />
                  {option}
                </li>
              );
            }}
          />
        </FilterItem>
      </Box>
    </Box>
  );
}; 